import { Link } from "@tanstack/react-router";

import { UnifiedBrowseCard } from "@/web/routes/browse/-components/cards/UnifiedBrowseCard";
import type { OfferingBrowseResult } from "@/web/routes/browse/-components/types";

/**
 * A card whose TTW page has been promoted to an open raise — the `fundraising`
 * stage of the unified browse card.
 *
 * It ALWAYS links out to `/ttw/<cardId>`, with no pledge-dialog branch. That is
 * the whole point of the stage: the page it lands on already carries the Invest
 * CTA and the checkout flow, so browse's job is only to get the visitor there.
 * (Contrast `SharableBusinessCardCard`, which opens an inline pledge dialog when
 * the card has no page to link to — an offering can only exist ON a page, so
 * that case is unreachable here.)
 *
 * There is no locked branch either. An access-coded card can still be promoted,
 * and the gate lives on `/ttw/<cardId>` exactly as it does for every other
 * stage — but the raise's progress is deliberately NOT masked the way pledge
 * totals are. Pledge totals are private community signal; a live raise's percent
 * funded is public offering information.
 */
export function OfferingCard({ browseResult }: { browseResult: OfferingBrowseResult }) {
  const { sharableBusinessCard } = browseResult;

  // Clamped 0–100 here rather than in the card so an over-subscribed raise
  // (committed money above target — legitimate, since the offering's hard cap is
  // `maxAmountCents`, not `targetAmountCents`) reads as a full bar instead of
  // overflowing it. A target of 0 would be a division by zero; treat it as 0%
  // rather than NaN, which renders as a blank bar and a blank footer.
  const percentFunded =
    browseResult.targetAmount > 0
      ? Math.min((browseResult.totalInvested / browseResult.targetAmount) * 100, 100)
      : 0;

  const visualProps = {
    stage: "fundraising" as const,
    name: sharableBusinessCard.name,
    category: browseResult.businessTypeText || undefined,
    imageCdnPath: sharableBusinessCard.heroCdnPath,
    tint: sharableBusinessCard.colors?.primary,
    percentFunded,
    backerCount: browseResult.totalInvestors,
    "data-testid": `offering-card-${sharableBusinessCard.cardId}`,
  };

  return (
    // `data-browse-card-action` lets the browse map forward a pin click to this
    // card's primary action, matching the other stages.
    <Link
      to="/ttw/$slug"
      params={{ slug: sharableBusinessCard.cardId }}
      target="_blank"
      rel="noopener noreferrer"
      className="block transition-opacity hover:opacity-95 focus-visible:outline-2 focus-visible:outline-offset-2"
      aria-label={`Invest in ${sharableBusinessCard.name}`}
      data-browse-card-action
    >
      {browseResult.hasTtwPageTargetProperty ? (
        <UnifiedBrowseCard entity="deal" address={browseResult.location} {...visualProps} />
      ) : (
        <UnifiedBrowseCard entity="business" {...visualProps} />
      )}
    </Link>
  );
}
